class VariantInlineSelect extends HTMLElement {
  connectedCallback() {
    if (this.abortController) {
      return;
    }

    this.abortController = new AbortController();
    this.trigger = this.querySelector(".variant-picker__inline-select-trigger");
    this.list = this.querySelector(".variant-picker__inline-select-list");
    this.options = Array.from(this.querySelectorAll("[data-option-value]"));

    this.trigger?.addEventListener("click", () => this.toggle(), { signal: this.abortController.signal });
    this.trigger?.addEventListener("keydown", (event) => this.onTriggerKeydown(event), {
      signal: this.abortController.signal,
    });

    this.options.forEach((option) => {
      option.addEventListener("click", () => this.selectOption(option), {
        signal: this.abortController.signal,
      });
      option.addEventListener("keydown", (event) => this.onOptionKeydown(event), {
        signal: this.abortController.signal,
      });
    });

    this.addEventListener(
      "focusout",
      (event) => {
        if (!this.contains(event.relatedTarget)) {
          this.close();
        }
      },
      { signal: this.abortController.signal },
    );

    document.addEventListener(
      "pointerdown",
      (event) => {
        if (!this.contains(event.target)) {
          this.close();
        }
      },
      { signal: this.abortController.signal },
    );
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
  }

  toggle() {
    if (this.trigger?.getAttribute("aria-expanded") === "true") {
      this.close();
    } else {
      this.open();
    }
  }

  open(focusOption = false) {
    if (!this.trigger || !this.list) {
      return;
    }

    this.list.hidden = false;
    this.trigger.setAttribute("aria-expanded", "true");

    if (focusOption) {
      (this.options.find((option) => option.hasAttribute("data-selected")) || this.options[0])?.focus();
    }
  }

  close({ restoreFocus = false } = {}) {
    if (!this.trigger || !this.list) {
      return;
    }

    this.list.hidden = true;
    this.trigger.setAttribute("aria-expanded", "false");

    if (restoreFocus) {
      this.trigger.focus();
    }
  }

  selectOption(option) {
    const optionFieldset = this.closest("fieldset");
    const matchingInput = Array.from(optionFieldset?.querySelectorAll("input[data-option-position]") || []).find(
      (input) => input.value === option.dataset.optionValue,
    );

    if (!matchingInput) {
      return;
    }

    this.querySelector("[data-selected-value]").textContent = option.querySelector("span")?.textContent.trim() || "";
    this.options.forEach((item) => {
      const isSelected = item === option;
      item.toggleAttribute("data-selected", isSelected);
      item.setAttribute("aria-selected", isSelected ? "true" : "false");
    });
    this.close({ restoreFocus: true });

    if (!matchingInput.checked) {
      matchingInput.checked = true;
      matchingInput.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  onTriggerKeydown(event) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      this.open(true);
    } else if (event.key === "Escape") {
      event.preventDefault();
      this.close();
    }
  }

  onOptionKeydown(event) {
    const currentIndex = this.options.indexOf(event.currentTarget);
    let nextIndex;

    switch (event.key) {
      case "ArrowDown":
        nextIndex = (currentIndex + 1) % this.options.length;
        break;
      case "ArrowUp":
        nextIndex = (currentIndex - 1 + this.options.length) % this.options.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = this.options.length - 1;
        break;
      case "Escape":
        event.preventDefault();
        this.close({ restoreFocus: true });
        return;
      default:
        return;
    }

    event.preventDefault();
    this.options[nextIndex]?.focus();
  }
}

if (!customElements.get("variant-inline-select")) {
  customElements.define("variant-inline-select", VariantInlineSelect);
}
